import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const navigate = useNavigate();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const websiteAlt = (formData.get('website_alt') as string || '').trim();
    if (websiteAlt) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt' && value) {
          params.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9ag714nib0mhnm9k490', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Something went wrong.';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-primary-900 text-background-50/70">
      <div className="px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-12">
            <div>
              <span className="font-heading text-xl font-bold text-background-50 whitespace-nowrap">
                Superbuy Spreadsheet
              </span>
              <p className="text-sm text-background-50/50 mt-2 max-w-xs">
                Independent educational resource for smarter online shopping.
              </p>
            </div>
            <p className="text-sm text-background-50/50 max-w-sm lg:text-right">
              Helping shoppers discover, organize, compare, and understand Superbuy spreadsheet resources since 2026.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">Shop Categories</h4>
              <div className="flex flex-col gap-2.5">
                {categories.slice(0, 5).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/50 hover:text-background-50/80 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">More Categories</h4>
              <div className="flex flex-col gap-2.5">
                {categories.slice(5, 10).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/50 hover:text-background-50/80 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">Resources</h4>
              <div className="flex flex-col gap-2.5">
                <button
                  onClick={() => navigate('/superbuy-spreadsheet-guide')}
                  className="text-sm text-background-50/50 hover:text-background-50/80 transition-colors text-left cursor-pointer whitespace-nowrap"
                >
                  Complete Guide
                </button>
                {categories.slice(10).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/50 hover:text-background-50/80 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/50 hover:text-background-50/80 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Main Website
                </a>
              </div>
            </div>

            <div className="lg:text-right">
              <p className="font-heading text-lg text-background-50/90 mb-1">
                &copy; 2026 Superbuy Spreadsheet Hub
              </p>
              <p className="text-sm text-background-50/50 mb-4">All rights reserved.</p>
              <div className="flex gap-4 lg:justify-end">
                <a href="#" className="text-sm text-background-50/50 hover:text-background-50/80 underline cursor-pointer">Twitter</a>
                <a href="#" className="text-sm text-background-50/50 hover:text-background-50/80 underline cursor-pointer">Pinterest</a>
                <a href="#" className="text-sm text-background-50/50 hover:text-background-50/80 underline cursor-pointer">Reddit</a>
                <a href="#" className="text-sm text-background-50/50 hover:text-background-50/80 underline cursor-pointer">YouTube</a>
              </div>
            </div>
          </div>

          <div className="border-t border-background-50/10 pt-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div className="lg:w-1/4">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20product%20flat%20lay%20with%20neutral%20toned%20fashion%20items%20on%20light%20wood%2C%20soft%20natural%20light%2C%20warm%20beige%20and%20cream%20tones%2C%20scandinavian%20aesthetic%2C%20clean%20editorial%20photography%2C%20organized%20composition&width=400&height=300&seq=footer-product-2026&orientation=landscape"
                  alt="Superbuy Spreadsheet - smart shopping and organized lifestyle"
                  className="w-full rounded-2xl object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
              <div className="lg:w-1/4">
                <p className="font-heading text-xl text-background-50/60 leading-relaxed">
                  Smart Shopping.
                  <br />
                  Organized Data.
                  <br />
                  Better Life.
                </p>
              </div>
              <div className="lg:w-1/2">
                <p className="text-sm text-background-50/50 mb-3">
                  Stay updated with the latest Superbuy Spreadsheet resources and guides.
                </p>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form="d9ag714nib0mhnm9k490"
                >
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <input
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        required
                        className="w-full px-5 py-3 rounded-full bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-50/30 focus:outline-none focus:border-background-50/40 transition-colors"
                      />
                    </div>
                    <div className="field-verify-wrapper">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <span>Subscribe</span>
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-400 mt-2">Thanks for subscribing! We will keep you updated.</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-400 mt-2">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-background-50/10 text-center">
            <p className="text-xs text-background-50/40">
              Superbuy Spreadsheet Hub is an independent educational resource and is not affiliated with Superbuy official services. All product links direct to third-party websites.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}